import { FC } from 'react';
import { cn } from '@/shared/lib';
import {
  isResolvedChart,
  type ChartAppearanceValues,
  type PresetChartFormValues,
} from '../../model/dashboardPresetForm';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { CHART_CELL_GUTTER_CLASS, CHART_SIZE_COL_SPAN } from '../DashboardPresetGrid/chartLayout';
import type { CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import type { SourceChartOption } from './ChartBindingFields';
import { ChartPropertiesForm } from './ChartPropertiesForm';
import { NewChartDescriptionField } from './NewChartDescriptionField';

type Props = {
  /** Диаграмма, свойства которой развёрнуты */
  chart: PresetChartFormValues;
  /** Фильтры панели страницы — ими наполнена та же диаграмма на сетке */
  filters: CustomChartPanelFilters;
  /** Прочие диаграммы дашборда: любая из них может стать ведущей для этой */
  sourceCharts: SourceChartOption[];
  /** Истолкованное описание — диаграмма встаёт на своё место предпросмотром сразу */
  onResolve: (chart: PresetChartFormValues) => void;
  /** Введённое описание остаётся за диаграммой, когда блок сворачивают, не сохранив свойств */
  onKeepDescription: (chartId: string, description: string) => void;
  /** Размер, выбранный в свойствах, диаграмма принимает сразу */
  onResize: (change: ChartSizeChange) => void;
  /** Оформление, заданное в свойствах, диаграмма принимает сразу */
  onRestyle: (appearance: ChartAppearanceValues) => void;
  /** Правки свойств приняты — диаграмма возвращается в состав дашборда */
  onSave: (chart: PresetChartFormValues) => void;
  onCancel: () => void;
};

/**
 * Свойства диаграммы блоком под её строкой на сетке — вместо окна поверх страницы: пользователь
 * меняет описание, тип или размер и тут же видит над блоком, что из этого вышло.
 *
 * Блок занимает всю ширину сетки, какой бы долей ни была сама диаграмма: полям свойств доли
 * показателя мало — описание набиралось бы в щель шириной с него.
 *
 * Заводимая диаграмма заполняется тем же блоком, но правила за ней ещё нет, и в блоке пока одно
 * поле — описание нужных данных. Истолкование наполняет диаграмму, и блок раскрывается остальными
 * полями, которые дальше правятся наравне с описанием.
 */
export const ChartPropertiesBlock: FC<Props> = ({
  chart,
  filters,
  sourceCharts,
  onResolve,
  onKeepDescription,
  onResize,
  onRestyle,
  onSave,
  onCancel,
}) => (
  <div
    data-testid="chart-properties-block"
    className={cn('grid min-w-0', CHART_SIZE_COL_SPAN.full, CHART_CELL_GUTTER_CLASS)}
  >
    <div className="@container/chart-properties min-w-0 rounded-xl border bg-card p-4">
      {isResolvedChart(chart) ? (
        <ChartPropertiesForm
          chart={chart}
          filters={filters}
          sourceCharts={sourceCharts}
          onResolve={onResolve}
          onKeepDescription={onKeepDescription}
          onResize={onResize}
          onRestyle={onRestyle}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : (
        <NewChartDescriptionField chart={chart} onResolve={onResolve} />
      )}
    </div>
  </div>
);
